"use client";

import Image from "next/image";
import { China, Eng, logoHeader, Thai } from "../assets";
import { GoHome } from "react-icons/go";
import { useRouter } from "next/navigation";
import { useLocale } from "next-intl";
import { useEffect, useRef, useState } from "react";
import { MdKeyboardArrowDown } from "react-icons/md";
import type { AuthProvider, AuthStatus, AuthUser } from "../hooks/useAuth";
import TeacherLogin from "./teacherLogin";
import { HiBars3 } from "react-icons/hi2";

const SUPPORTED_LOCALES = ["th", "en", "cn"] as const;
type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];

const LOCALE_ICON: Record<SupportedLocale, typeof Thai> = {
	th: Thai,
	en: Eng,
	cn: China,
};

const LOCALE_ALT: Record<SupportedLocale, string> = {
	th: "Thai",
	en: "English",
	cn: "Chinese",
};

const LOCALE_LABEL: Record<SupportedLocale, string> = {
	// th: "TH",
	// en: "EN",
	// cn: "CN",
	th: "Thai",
	en: "English",
	cn: "Chinese",
};

const HIDDEN_LOCALE_SWITCHER_ROLES = new Set(["super_admin", "admin"]);
const THAI_PILA_HOME_URL = "https://thaipila.eef.or.th/";

const openThaiPilaHome = () => {
	window.open(THAI_PILA_HOME_URL, "_blank", "noopener,noreferrer");
};

type HeaderProps = {
	authStatus: AuthStatus;
	user: AuthUser | null;
	onLogin: (provider: AuthProvider) => void | Promise<void>;
	onLogout: () => void | Promise<void>;
	onToggleSidebar?: () => void;
};

export default function Header({ authStatus, user, onLogin, onLogout, onToggleSidebar }: HeaderProps) {
	const router = useRouter();
	const locale = useLocale();
	const currentLocale = (SUPPORTED_LOCALES.includes(locale as SupportedLocale) ? locale : "th") as SupportedLocale;
	const currentLocaleIcon = LOCALE_ICON[currentLocale];
	const localeIconClassName = currentLocale === "cn"
		? "h-8 w-8 scale-115"
		: "h-8 w-8";
	const hasUser = authStatus === "authenticated" && !!user;
	const userRole = String(user?.role ?? "").trim().toLowerCase();
	const shouldHideLocaleSwitcher = hasUser && HIDDEN_LOCALE_SWITCHER_ROLES.has(userRole);
	const [isLocaleMenuOpen, setIsLocaleMenuOpen] = useState(false);
	const localeMenuRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (!isLocaleMenuOpen) {
			return;
		}

		const handleClickOutside = (event: MouseEvent) => {
			if (!localeMenuRef.current?.contains(event.target as Node)) {
				setIsLocaleMenuOpen(false);
			}
		};

		const handleEscape = (event: KeyboardEvent) => {
			if (event.key === "Escape") {
				setIsLocaleMenuOpen(false);
			}
		};

		document.addEventListener("mousedown", handleClickOutside);
		window.addEventListener("keydown", handleEscape);

		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
			window.removeEventListener("keydown", handleEscape);
		};
	}, [isLocaleMenuOpen]);

	const handleSelectLocale = (nextLocale: SupportedLocale) => {
		setIsLocaleMenuOpen(false);
		if (nextLocale === currentLocale) {
			return;
		}

		document.cookie = `eef_locale=${nextLocale}; path=/;`;
		router.refresh();
	};

	return (
		<>
			<header className="relative z-20 h-16 w-full bg-[#ffffff] shadow-[0_2px_10px_rgba(0,0,0,0.12)]">
				<div className="mx-auto flex h-full max-w-460 items-center justify-between px-3 sm:px-4">
					<div className="flex items-center gap-2">
						{hasUser ? (
							<button
								type="button"
								onClick={onToggleSidebar}
								className="inline-flex h-9 w-9 items-center justify-center rounded-md text-neutral-700 hover:bg-neutral-100 lg:hidden"
								aria-label="Toggle sidebar"
							>
								<HiBars3 size={20} />
							</button>
						) : null}
						<button
							type="button"
							className="mt-2 flex items-center"
							onClick={openThaiPilaHome}
							aria-label="Thai Pila home"
						>
							<Image src={logoHeader} alt="Thai Pila" priority className="h-8 w-auto" />
						</button>
					</div>

					<div className="flex items-center gap-2 sm:gap-4 text-sm text-neutral-700">
						<button
							type="button"
							onClick={openThaiPilaHome}
							className="inline-flex h-9 w-9 items-center justify-center rounded-md text-neutral-700 hover:bg-neutral-100"
							aria-label="Go to Thai Pila home"
						>
							<GoHome size={24} />
						</button>
						{!shouldHideLocaleSwitcher ? (
						<div ref={localeMenuRef} className="relative">
							<button
								type="button"
								className="flex items-center gap-1 rounded-md px-2 py-1 hover:bg-neutral-100"
								onClick={() => setIsLocaleMenuOpen((prev) => !prev)}
								aria-label="Change language"
								aria-expanded={isLocaleMenuOpen}
								aria-haspopup="listbox"
							>
								<Image
									src={currentLocaleIcon}
									alt={LOCALE_ALT[currentLocale]}
									className={localeIconClassName}
								/>
								<MdKeyboardArrowDown
									size={18}
									className={`text-neutral-500 transition-transform ${isLocaleMenuOpen ? "rotate-180" : ""}`}
								/>
							</button>

							{isLocaleMenuOpen ? (
								<div
									role="listbox"
									aria-label="Select language"
									className="absolute right-0 top-full z-30 mt-1 min-w-[132px] overflow-hidden rounded-lg border border-[#e5e7eb] bg-white py-1 shadow-[0_8px_24px_rgba(0,0,0,0.12)]"
								>
									{SUPPORTED_LOCALES.map((localeOption) => {
										const isSelected = localeOption === currentLocale;
										const optionIconClassName = localeOption === "cn"
											? "h-7 w-7 scale-110"
											: "h-7 w-7";

										return (
											<button
												key={localeOption}
												type="button"
												role="option"
												aria-selected={isSelected}
												onClick={() => handleSelectLocale(localeOption)}
												className={`flex w-full cursor-pointer items-center gap-2.5 px-3 py-2 text-left text-sm font-medium transition-colors hover:bg-neutral-50 ${isSelected ? "bg-[#f5f8ff] text-[#007FC3]" : "text-neutral-700"}`}
											>
												<Image
													src={LOCALE_ICON[localeOption]}
													alt={LOCALE_ALT[localeOption]}
													className={optionIconClassName}
												/>
												<span>{LOCALE_LABEL[localeOption]}</span>
											</button>
										);
									})}
								</div>
							) : null}
						</div>
						) : null}

						{hasUser ? (
							<TeacherLogin
								mode="inline"
								status={authStatus}
								user={user}
								onLogin={onLogin}
								onLogout={onLogout}
							/>
						) : authStatus === "checking" ? (
							null
						) : (
							<div>
								{/* <button
								type="button"
								onClick={() => setShowLoginScreen(true)}
								className="rounded-lg bg-[#FDEF5C] px-4 py-2 transition hover:bg-[#fce94f] cursor-pointer"
							>
								<span className="text-[#2b2b2b] text-lg">เข้าสู่ระบบ</span>
							</button> */}
							</div>
						)}

					</div>
				</div>
			</header>
		</>
	);
}
